import { useEffect, useState } from "preact/hooks";
import { t } from "../i18n/index.ts";
import { type DropTarget, droppedFiles, dropping, dropTarget, uploadDropped } from "../lib/fileDrop.ts";
import { ignore } from "../lib/ignore.ts";
import { spaceUrl } from "../lib/paths.ts";
import { homeSpace, me } from "../lib/state.ts";
import { toast } from "../lib/toast.ts";
import { UploadIcon } from "./icons.tsx";

/** Files dragged in from outside the page (the desktop, another window). */
const hasFiles = (e: DragEvent): boolean => Array.from(e.dataTransfer?.types ?? []).includes("Files");

/** Something of the page that takes a drop itself, the browser's way: a document's text, a file field. */
const ownsDrop = (el: EventTarget | null): boolean =>
  el instanceof HTMLElement && (el.isContentEditable || (el instanceof HTMLInputElement && el.type === "file"));

/** Dropped where files can't go: why (a viewer of the space), or that they go into a space (yours a link away). */
function refuse(target: DropTarget | null): void {
  if (target) {
    toast(target.label);
    return;
  }
  const home = me.value ? homeSpace() : undefined;
  const links = home ? [{ label: t("drop.openTriage"), href: spaceUrl({ id: home }, "/triage") }] : undefined;
  toast(t("drop.nowhere"), "info", undefined, links);
}

/**
 * Files dragged over the window: a veil over it saying what dropping them does (lib/fileDrop.ts has where they go),
 * then the upload under way in a corner. Decorative: what came of it is said in a toast. What of the page takes a
 * drop itself (a drop zone, a board's column, a document's text) keeps it; something of the page dragged in it (a
 * card, a picture, text) is left alone.
 */
export function FileDrop() {
  // What dropping now would do (null: nothing dragged in, or something under it takes the drop).
  const [shown, setShown] = useState<{ label: string; ok: boolean } | null>(null);
  useEffect(() => {
    // How deep in the window's elements the drag is (it's left the window at 0), and whether it began in the page.
    let depth = 0;
    let inPage = false;
    const theirs = (e: DragEvent) => inPage || !hasFiles(e);
    const start = () => {
      inPage = true;
    };
    const end = () => {
      inPage = false;
      depth = 0;
      setShown(null);
    };
    const enter = (e: DragEvent) => {
      if (!theirs(e)) {
        depth += 1;
      }
    };
    const leave = (e: DragEvent) => {
      if (theirs(e)) {
        return;
      }
      depth -= 1;
      if (depth <= 0) {
        depth = 0;
        setShown(null);
      }
    };
    const over = (e: DragEvent) => {
      if (theirs(e)) {
        return;
      }
      if (e.defaultPrevented || ownsDrop(e.target)) {
        setShown(null);
        return;
      }
      // Taken here (the browser would open the file otherwise, leaving the app).
      e.preventDefault();
      if (e.dataTransfer) {
        e.dataTransfer.dropEffect = "copy";
      }
      const target = dropTarget();
      const next = { label: target?.label ?? t("drop.nowhere"), ok: Boolean(target?.take) };
      setShown((s) => (s?.label === next.label && s.ok === next.ok ? s : next));
    };
    const drop = (e: DragEvent) => {
      const skip = theirs(e) || e.defaultPrevented || ownsDrop(e.target);
      end();
      if (skip) {
        return;
      }
      e.preventDefault();
      const target = dropTarget();
      if (target?.take) {
        // A dropped folder's files, each with its path in it.
        droppedFiles(e.dataTransfer)
          .then((files) => uploadDropped(target, files))
          .catch(ignore);
      } else {
        refuse(target);
      }
    };
    const on: [string, (e: DragEvent) => void][] = [
      ["dragstart", start],
      ["dragend", end],
      ["dragenter", enter],
      ["dragleave", leave],
      ["dragover", over],
      ["drop", drop],
    ];
    for (const [type, fn] of on) {
      globalThis.addEventListener(type, fn as EventListener);
    }
    return () => {
      for (const [type, fn] of on) {
        globalThis.removeEventListener(type, fn as EventListener);
      }
    };
  }, []);
  const busy = dropping.value.at(-1);
  return (
    <>
      {/* In the iOS app (`ios:`): as iOS shows where a drop goes, only the page tinted in the accent within a rounded
          outline (no dashes, nothing said over it); the upload under way in a capsule. */}
      {shown ? (
        <div
          aria-hidden="true"
          data-testid="file-drop"
          class="pointer-events-none fixed inset-0 z-50 flex items-center justify-center bg-zinc-900/15 p-4 ios:bg-transparent"
        >
          <div class="absolute inset-3 rounded-xl border-2 border-dashed border-blue-500 bg-blue-50/70 ios:inset-2 ios:rounded-[1.75rem] ios:border-solid ios:border-blue-500/70 ios:bg-blue-500/10" />
          <p class="relative flex max-w-md items-center gap-3 rounded-lg border border-zinc-200 bg-white px-5 py-4 font-medium text-zinc-900 shadow-lg ios:hidden">
            <UploadIcon size={24} class={shown.ok ? "text-blue-600" : "text-zinc-400"} />
            {shown.label}
          </p>
        </div>
      ) : null}
      {busy ? (
        <p
          aria-hidden="true"
          class="pointer-events-none fixed bottom-4 left-4 z-50 flex items-center gap-2 rounded-md border border-on-toast/10 bg-toast px-4 py-3 text-sm text-on-toast shadow-lg ios:bottom-[max(1rem,env(safe-area-inset-bottom))] ios:rounded-full ios:border-transparent ios:shadow-xl"
        >
          <span class="inline-block size-3.5 animate-spin rounded-full border-2 border-current border-r-transparent" />
          {busy.busy}
        </p>
      ) : null}
    </>
  );
}
